// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Correcting or revoking a maintenance record while it is still in its window (design doc
 * §8.6.3): for two hours after it is submitted, and only while nothing has been recorded on
 * the machine after it.
 *
 * A wrong serial typed into a change used to need a second change to put it right, leaving
 * the mistake and its correction side by side in the history. Here the record itself is
 * corrected: **编辑** opens the change form on the machine as it was before the record,
 * filled in with what the record did, and saving replaces it — same number, same link.
 * **撤销** takes it back altogether.
 *
 * The buttons say how long is left. They are offered on the record's own archive only while
 * it is that archive's latest version; the server is the authority either way (a change
 * inside a module may have moved the robot since), and its refusal is shown as it comes.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { newIdempotencyKey } from "@/api/client";
import { api } from "@/api/endpoints";
import type { ArchiveDetail, MaintenanceEvent } from "@/api/types";
import { usePermissions } from "@/hooks/usePermissions";
import { changeText } from "./ChangeType";
import { ErrorBanner } from "./ErrorBanner";
import {
  type CorrectionDraft,
  correctionDraft,
  MaintenanceDialog,
  type MaintenanceSubmission,
} from "./MaintenanceDialog";
import { Modal } from "./Modal";

const TICK_MS = 30_000;

/** Now, kept current while something on screen counts down. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

/** Whether a record can still be corrected, as far as the page can tell. */
export function isCorrectable(
  event: MaintenanceEvent,
  now: number,
  currentVersion?: number,
): boolean {
  if (!event.editable_until || Date.parse(event.editable_until) <= now) return false;
  return currentVersion === undefined || event.result_version === currentVersion;
}

interface Editing {
  archive: ArchiveDetail;
  draft: CorrectionDraft;
  occurredAt: string;
}

export function MaintenanceCorrection({
  archiveId,
  event,
  currentVersion,
  onRevoked,
}: {
  archiveId: string;
  event: MaintenanceEvent;
  /** The archive's current version, when known: a record below it is final. */
  currentVersion?: number;
  onRevoked?: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { canRecord } = usePermissions();
  const now = useNow(Boolean(event.editable_until));
  const [editing, setEditing] = useState<Editing | null>(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [notEditable, setNotEditable] = useState(false);

  async function refreshEverything() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["archive"] }),
      queryClient.invalidateQueries({ queryKey: ["archive-versions"] }),
      queryClient.invalidateQueries({ queryKey: ["maintenance"] }),
      queryClient.invalidateQueries({ queryKey: ["maintenance-event"] }),
      queryClient.invalidateQueries({ queryKey: ["archive-installations"] }),
    ]);
  }

  const open = useMutation({
    mutationFn: async (): Promise<Editing | null> => {
      const view = await api.maintenance.submission(archiveId, event.event_id);
      const draft = correctionDraft(view, t("maintenance.correctTitle", { n: view.sequence_no }));
      if (!draft) return null;
      const before = await api.archives.get(view.open_archive_id, true, view.base_version);
      // The form reads the version it shows as the one it builds on.
      return {
        archive: { ...before, current_version: view.base_version },
        draft,
        occurredAt: view.occurred_at,
      };
    },
    onSuccess: (result) => {
      if (result) setEditing(result);
      else setNotEditable(true);
    },
  });

  const save = useMutation({
    mutationFn: (submission: MaintenanceSubmission) => {
      if (!editing) throw new Error("not editing");
      return api.maintenance.edit(
        archiveId,
        event.event_id,
        {
          // Untouched, the time it had — to the second, not to the minute the field shows.
          // Cleared, the time of this edit by the server's clock (sent as no time).
          occurred_at:
            submission.occurredAt === editing.draft.occurredAt
              ? editing.occurredAt
              : submission.occurredAt
                ? new Date(submission.occurredAt).toISOString()
                : null,
          change_type: submission.changeType,
          reason: submission.reason,
          jira_url: submission.jiraUrl,
          lines: submission.lines,
        },
        newIdempotencyKey(),
      );
    },
    onSuccess: async () => {
      setEditing(null);
      await refreshEverything();
    },
  });

  const revoke = useMutation({
    mutationFn: () => api.maintenance.revoke(archiveId, event.event_id),
    onSuccess: async () => {
      setConfirmingRevoke(false);
      await refreshEverything();
      onRevoked?.();
    },
  });

  if (!canRecord || !isCorrectable(event, now, currentVersion)) return null;
  const minutes = Math.max(1, Math.ceil((Date.parse(event.editable_until ?? "") - now) / 60_000));

  return (
    <span className="correction">
      <span className="badge badge-correctable" title={t("maintenance.correctableHint")}>
        {t("maintenance.correctableFor", {
          time: new Date(event.editable_until ?? "").toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          minutes,
        })}
      </span>
      <button type="button" disabled={open.isPending} onClick={() => open.mutate()}>
        {t("maintenance.correct")}
      </button>
      <button type="button" className="button-danger" onClick={() => setConfirmingRevoke(true)}>
        {t("maintenance.revoke")}
      </button>
      {(open.error || notEditable) && (
        <span className="correction-error">
          {notEditable ? (
            <span className="hint hint-error">{t("maintenance.correctUnsupported")}</span>
          ) : (
            <ErrorBanner error={open.error} />
          )}
        </span>
      )}

      {/* Out of the table cell this may sit in, whose one-line styling the dialogs would
          otherwise inherit. React events still bubble to the cell, which stops them. */}
      {createPortal(
        <>
          {editing && (
            <MaintenanceDialog
              archive={editing.archive}
              correcting={editing.draft}
              pending={save.isPending}
              error={<ErrorBanner error={save.error} />}
              onSubmit={(submission) => save.mutate(submission)}
              onClose={() => {
                save.reset();
                setEditing(null);
              }}
            />
          )}

          {confirmingRevoke && (
            <Modal
              title={t("maintenance.revokeTitle", { n: event.sequence_no })}
              onClose={() => {
                revoke.reset();
                setConfirmingRevoke(false);
              }}
              footer={
                <>
                  <button
                    type="button"
                    className="button-danger"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate()}
                  >
                    {t("maintenance.revoke")}
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    onClick={() => {
                      revoke.reset();
                      setConfirmingRevoke(false);
                    }}
                  >
                    {t("common.cancel")}
                  </button>
                </>
              }
            >
              <p>
                {t("maintenance.revokeBody", {
                  reason: changeText(t, event.change_type, event.reason),
                })}
              </p>
              <ErrorBanner error={revoke.error} />
            </Modal>
          )}
        </>,
        document.body,
      )}
    </span>
  );
}
